// Mall API client — talks to ppGateway (same origin via nginx, or the Vite
// dev proxy). Public catalogue calls need no auth; order calls use the
// Firebase ID token as Bearer, or the gateway session key handed over by the
// POS app (see ./sso.ts) as X-Session-Key.
import { getIdToken } from './firebase.js';
import { handoffSessionKey } from './sso.js';

export class ApiError extends Error {
  status: number;
  /** Gateway error code, e.g. PROFILE_REQUIRED / OUTLET_OFFLINE. */
  code: string;
  constructor(status: number, message: string, code = '') {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const API_BASE = '/api';

export interface MallItem {
  id: string;
  code: string;
  name: string;
  description?: string;
  typeName?: string;
  qoh: number;
  qohString?: string;
  price: number;
  barcode?: string;
  packTypeId?: string;
  storeCode: string;
  storeName?: string;
  /** False when this item comes from the cached catalogue of an offline outlet. */
  storeOnline?: boolean;
  /** False when the QOH shown is a stale snapshot (offline outlet). */
  qohKnown?: boolean;
  purchasable?: boolean;
  listingEnd?: number;
  imageUrl?: string;
  thumbnailUrl?: string;
  imageUrls?: string[];
  packs?: Array<{
    packTypeId: string;
    packDescription: string;
    packSize: number;
    sellingPrice: number;
    membershipPrice: number;
    barcode: string;
  }>;
  stockId?: string;
  published?: boolean;
  inStore?: boolean;
  /** Gateway taxonomy subcategory ids (empty = legacy uncategorised listing). */
  categoryIds?: string[];
  uncategorised?: boolean;
  categories?: Array<{ id: string; parentId: string; name: string; slug: string }>;
}

/** One gateway-owned category/subcategory (flat list; group by parentId). */
export interface MallCategory {
  id: string;
  parentId: string;
  name: string;
  slug: string;
  sortOrder: number;
  system?: boolean;
  itemCount?: number;
}

export interface MallOrder {
  id: string;
  refNo: string;
  storeCode: string;
  total: number;
  paymentMode: string;
  status: string;
  billCode?: string;
  paymentProvider?: string;
  paymentStatus?: string;
  paymentCustomerAmount?: number;
  paymentFee?: number;
  paymentSettledAt?: number;
  /** "member" (Hold Sales at the counter) | "customer" (Hold Wholesales). */
  shopperType?: string;
  /** "hold" (new orders) | "order" (legacy SalesOrder). */
  kind?: string;
  /** Counter hold behind the order (empty while it is still queued). */
  orderId?: string;
  /** Why the order is queued / was rejected (shown to the shopper). */
  message?: string;
  createdAt: number;
  items: Array<{ stockId: string; code: string; name: string; quantity: number; price: number }>;
}

export interface MallOutlet {
  code: string;
  name: string;
  location?: string;
  hasCoordinates?: boolean;
  latitude?: number;
  longitude?: number;
  distanceKm?: number;
  itemCount: number;
  online: boolean;
  rev?: number;
}

/** Who owns the signed-in email at this outlet. */
export interface MallShopperProfile {
  email: string;
  found: boolean;
  shopperType: string;   // "member" | "customer" | ""
  isMember: boolean;
  isCustomer: boolean;
  name: string;
  companyName: string;
  address: string;
  phone: string;
}

/** Profile fields collected from a first-time shopper. */
export interface MallShopperInput {
  shopperType?: 'member' | 'customer';
  name?: string;
  companyName?: string;
  address?: string;
  phone?: string;
}

async function request<T>(method: string, path: string, body?: unknown, auth = false): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (auth) {
    const token = await getIdToken(true);
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Signed in by the POS app handoff instead of a Firebase session on this
    // origin. The gateway prefers the session key when both are present.
    const sessionKey = handoffSessionKey();
    if (sessionKey) headers['X-Session-Key'] = sessionKey;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    throw new ApiError(res.status, data?.error?.message || data?.error || `Request failed (${res.status})`, data?.error?.code);
  }
  // Gateway returns {success, data} envelopes for Router responses.
  if (data && typeof data === 'object' && 'success' in data) {
    if (!data.success) throw new ApiError(res.status, data.error?.message || 'Request failed', data.error?.code);
    return data.data as T;
  }
  return data as T;
}

export const api = {
  // ── Public catalogue ──
  getOutlets: () => request<MallOutlet[]>('GET', '/mall/outlets'),

  /** Outlet discovery: name/code/location search, optional distance ranking. */
  searchOutlets: (params: {
    q?: string;
    lat?: number;
    lng?: number;
    radiusKm?: number;
    page?: number;
    pageSize?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.lat !== undefined && params.lng !== undefined) {
      q.set('lat', String(params.lat));
      q.set('lng', String(params.lng));
      if (params.radiusKm !== undefined) q.set('radiusKm', String(params.radiusKm));
    }
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    return request<{ outlets: MallOutlet[]; total: number; page: number; pageSize: number }>(
      'GET', `/mall/outlets/search?${q.toString()}`);
  },

  /** Gateway-owned master taxonomy for this mall deployment. */
  getCategories: () => request<MallCategory[]>('GET', '/mall/categories'),

  getCatalog: (params: { search?: string; store?: string; category?: string; page?: number; pageSize?: number; sort?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.store) q.set('store', params.store);
    if (params.category) q.set('category', params.category);
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.sort) q.set('sort', params.sort);
    return request<{ items: MallItem[]; total: number; page: number; pageSize: number }>(
      'GET', `/mall/catalog?${q.toString()}`);
  },

  getItem: (storeCode: string, stockId: string) =>
    request<MallItem>('GET', `/mall/items/${encodeURIComponent(storeCode)}/${encodeURIComponent(stockId)}`),

  scanResolve: (storeCode: string, barcode: string) =>
    request<MallItem>('POST', '/mall/scan/resolve', { storeCode, barcode }),

  // ── Orders (signed in) ──
  loginGateway: (idToken: string) =>
    request<{ sessionKey: string; email: string }>('POST', '/auth/login', { idToken }),

  /** Validate the session key handed over by the POS app → signed-in email. */
  gatewaySession: () =>
    request<{ email: string; sessionKey: string }>('GET', '/auth/session', undefined, true),

  /** Can this outlet take online payments, and what fee applies? Public. */
  paymentOptions: (storeCode: string, total?: number) =>
    request<{
      storeCode: string;
      onlinePayment: boolean;
      provider: string;
      providerLabel: string;
      feeMode: string;
      feePercent: number;
      feeFixedCents: number;
      feeMinCents: number;
      feeMaxCents: number;
      feeAmount?: number;
      customerTotal?: number;
      message: string;
    }>('GET', `/mall/payment-options?storeCode=${encodeURIComponent(storeCode)}${total !== undefined ? `&total=${encodeURIComponent(String(total))}` : ''}`),

  /** Member / wholesale customer behind the signed-in email (checkout form). */
  profile: (storeCode: string) =>
    request<MallShopperProfile>('GET', `/mall/profile?storeCode=${encodeURIComponent(storeCode)}`, undefined, true),

  checkout: (payload: {
    storeCode: string;
    items: Array<{ stockId: string; quantity: number }>;
    payment: 'counter' | 'online';
  } & MallShopperInput) => request<{
    id: string; refNo?: string; orderId?: string; storeCode: string; total: number;
    status: string; paymentMode: string; paymentUrl?: string; warning?: string;
    paymentProvider?: string; paymentStatus?: string; feeAmount?: number; customerTotal?: number;
    shopperType?: string; customerName?: string; customerCode?: string;
    /** 202 `pending_confirmation` — saved while the outlet was offline. */
    ordersQueued?: boolean; message?: string;
  }>('POST', '/mall/checkout', payload, true),

  /** Complete an order that was saved while the outlet was offline.
   *  `payment` picks pay-at-counter or the outlet's payment gateway. */
  confirmOrder: (id: string, payment: 'counter' | 'online') =>
    request<MallOrder & { paymentUrl?: string; warning?: string }>(
      'POST', `/mall/orders/${encodeURIComponent(id)}/confirm`, { payment }, true),

  myOrders: () => request<MallOrder[]>('GET', '/mall/orders', undefined, true),

  orderDetail: (id: string) => request<MallOrder>('GET', `/mall/orders/${encodeURIComponent(id)}`, undefined, true),

  requeryOrder: (id: string) =>
    request<{
      status: string;
      billStatus?: string;
      paymentStatus?: string;
      provider?: string;
      paymentMode?: string;
      refNo?: string;
      total?: number;
      saleId?: string;
    }>('POST', `/mall/orders/${encodeURIComponent(id)}/requery`, undefined, true),

  cancelOrder: (id: string) =>
    request<{ status: string }>('POST', `/mall/orders/${encodeURIComponent(id)}/cancel`, undefined, true),

  // ── Push notifications (device registration) ──
  registerDevice: (fcmToken: string) =>
    request<{ message: string }>(
      'POST', '/auth/register-device', { fcmToken, platform: 'web' }, true),

  unregisterDevice: (fcmToken: string) =>
    request<{ message: string }>(
      'DELETE', '/auth/register-device', { fcmToken }, true),

  // ── In-store scan & go (signed in) ──
  instoreHold: (storeCode: string, items: Array<{ stockId: string; quantity: number }>, shopper: MallShopperInput = {}) =>
    request<{ holdId: string; storeCode: string; total: number; message: string; shopperType?: string }>(
      'POST', '/mall/instore/hold', { storeCode, items, ...shopper }, true),

  instoreRelease: (holdId: string, storeCode: string) =>
    request<{ status: string }>(
      'DELETE', `/mall/instore/hold/${encodeURIComponent(holdId)}?storeCode=${encodeURIComponent(storeCode)}`, undefined, true),
};
